(() => {
  if (customElements.get('cn-sticky-purchase')) return;
  const overlaySelector = 'cart-drawer.active, cart-notification.active, #cart-notification.active, quick-add-modal[open], product-modal[open], modal-dialog[open], dialog[open], [role="dialog"][aria-modal="true"]:not([hidden]):not([aria-hidden="true"])';
  const text = node => node?.textContent?.replace(/\s+/g, ' ').trim() || '';
  const write = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };
  const hidden = node => !node || Boolean(node.closest('[hidden], .hidden, [aria-hidden="true"]'));
  const overlayOpen = () => {
    if (document.body.classList.contains('overflow-hidden') || document.body.classList.contains('overflow-hidden-mobile')) return true;
    return [...document.querySelectorAll(overlaySelector)].some(node => {
      if (hidden(node) || node.closest('details:not([open]), cart-drawer:not(.active), quick-add-modal:not([open]), product-modal:not([open]), modal-dialog:not([open])')) return false;
      const style = getComputedStyle(node);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
  };

  class StickyPurchase extends HTMLElement {
    connectedCallback() {
      this.hidden = true;
      this.product = this.closest('product-info');
      if (!this.product || this.product.dataset.updateUrl !== 'true' || this.closest('quick-add-modal, [role="dialog"]')) return;
      this.button = this.querySelector('[data-cn-sticky-submit]');
      this.quantityButton = this.querySelector('[data-cn-sticky-quantity]');
      if (!this.button || !this.quantityButton) return;
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; if (this.isConnected) this.sync(); });
      };
      this.onBuy = () => {
        const original = this.originalButton();
        if (!original || original.disabled || original.getAttribute('aria-disabled') === 'true' || original.classList.contains('loading') || this.pricePending(original) || original.form?.querySelector('[name="id"]')?.disabled) return;
        // Native click preserves recipient validation, form-associated quantity,
        // line-item properties, selling plans, cart errors and cart focus return.
        original.click();
        this.sync();
      };
      this.onQuantity = () => {
        const input = this.quantityInput();
        if (!input) return;
        input.focus({ preventScroll: true });
        input.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      };
      this.onFocus = event => {
        const target = event.target;
        if (this.hidden || this.contains(target) || !target?.getBoundingClientRect || overlayOpen()) return;
        const targetRect = target.getBoundingClientRect();
        const barRect = this.getBoundingClientRect();
        // The browser can scroll focused controls underneath a fixed purchase bar.
        // Move only an actually covered control, without smooth motion or focus changes.
        if (targetRect.height > 0 && targetRect.bottom > barRect.top && targetRect.top < barRect.bottom) {
          window.scrollBy({ top: targetRect.bottom - barRect.top + 12, behavior: 'instant' });
        }
      };
      this.button.addEventListener('click', this.onBuy);
      this.quantityButton.addEventListener('click', this.onQuantity);
      this.product.addEventListener('input', this.schedule);
      this.product.addEventListener('change', this.schedule);
      window.addEventListener('scroll', this.schedule, { passive: true });
      window.addEventListener('resize', this.schedule);
      document.addEventListener('focusin', this.onFocus);
      window.visualViewport?.addEventListener('resize', this.schedule);
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.schedule();
      });
      this.observer.observe(this.product, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'hidden', 'disabled', 'aria-disabled', 'aria-hidden', 'value', 'data-cn-price-ready'] });
      this.overlays = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target) && (!this.product.contains(record.target) || record.attributeName === 'open'))) this.schedule();
      });
      this.overlays.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'open', 'hidden', 'aria-hidden', 'aria-modal', 'data-state'] });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.schedule);
        this.resizeObserver.observe(this);
        this.resizeObserver.observe(this.product);
      }
      this.sync();
    }

    originalButton() { return this.product?.querySelector('product-form button[type="submit"]'); }
    pricePending(original = this.originalButton()) {
      const input = original?.form?.querySelector('[name="id"]');
      if (input?.dataset.cnPriceReady === 'false' || original?.closest('product-form')?.dataset.cnPriceReady === 'false') return true;
      const data = this.product?.querySelector('[id^="price-"] [data-cn-purchase-pricing]');
      if (!data) return false;
      try {
        const pricing = JSON.parse(data.textContent);
        return String(pricing?.variantId) !== input?.value || !Number.isSafeInteger(pricing?.price) || pricing.price <= 0;
      } catch { return true; }
    }
    quantityInput() {
      const form = this.originalButton()?.form;
      const input = form?.elements.namedItem('quantity');
      return input?.tagName === 'INPUT' ? input : null;
    }

    sync() {
      const original = this.originalButton();
      if (!original) { this.show(false); return; }
      const busy = original.classList.contains('loading');
      const pricePending = this.pricePending(original);
      this.button.disabled = original.disabled || original.getAttribute('aria-disabled') === 'true' || busy || pricePending || Boolean(original.form?.querySelector('[name="id"]')?.disabled);
      this.button.setAttribute('aria-busy', String(busy));
      const originalLabel = [...original.children].find(child => child.tagName === 'SPAN' && !hidden(child) && !child.classList.contains('loading__spinner'));
      write(this.querySelector('[data-cn-sticky-submit-label]'), pricePending ? 'Preis noch nicht verfügbar' : text(originalLabel) || window.variantStrings?.addToCart || 'In den Warenkorb');
      if (original.hasAttribute('aria-haspopup')) this.button.setAttribute('aria-haspopup', original.getAttribute('aria-haspopup'));
      else this.button.removeAttribute('aria-haspopup');
      const heading = this.product.querySelector('.product__title h1, .product__title h2');
      write(this.querySelector('[data-cn-sticky-title]'), text(heading) || this.dataset.productTitle || 'Dein Produkt');
      let variantTitle = '';
      try { variantTitle = JSON.parse(this.product.querySelector('[data-selected-variant]')?.textContent || 'null')?.title || ''; } catch { /* Native option text remains a safe fallback. */ }
      if (!variantTitle) variantTitle = [...this.product.querySelectorAll('variant-selects input:checked, variant-selects option:checked')].map(node => node.value).filter(Boolean).join(' · ');
      if (variantTitle === 'Default Title') variantTitle = '';
      const variant = this.querySelector('[data-cn-sticky-variant]');
      write(variant, variantTitle); variant.hidden = !variantTitle;
      const priceHost = this.product.querySelector('[id^="price-"]');
      const price = priceHost?.querySelector('.price--on-sale .price__sale .price-item--sale') || priceHost?.querySelector('.price__regular .price-item--regular:not(s)');
      const priceText = hidden(priceHost) ? '' : pricePending ? 'Preis noch nicht verfügbar' : text(price);
      const priceTarget = this.querySelector('[data-cn-sticky-price]');
      write(priceTarget, priceText); priceTarget.closest('.cn-sticky-price').hidden = !priceText;
      // Read raw Shopify money amounts from the same refreshed price fragment.
      // Formatted currency text is copied for display only, never parsed.
      let pricing = null;
      try { pricing = JSON.parse(priceHost?.querySelector('[data-cn-purchase-pricing]')?.textContent || 'null'); } catch { /* Invalid or missing data must not invent a discount. */ }
      const selectedId = original.form?.querySelector('[name="id"]')?.value;
      const compareText = text(priceHost?.querySelector('[data-cn-purchase-compare]'));
      const sale = Boolean(priceText && compareText && selectedId && String(pricing?.variantId) === selectedId && !pricing?.volumePricing && Number.isSafeInteger(pricing?.price) && Number.isSafeInteger(pricing?.compareAt) && pricing.price > 0 && pricing.compareAt > pricing.price);
      const discount = sale ? Math.floor((pricing.compareAt - pricing.price) * 100 / pricing.compareAt) : 0;
      const discountRow = this.querySelector('[data-cn-sticky-discount-row]');
      const oldPrice = this.querySelector('[data-cn-sticky-original-price]');
      const badge = this.querySelector('[data-cn-sticky-discount]');
      discountRow.hidden = !sale;
      write(oldPrice, sale ? compareText : '');
      const badgeText = sale ? (discount > 0 ? `−${discount} %` : '<1 % Rabatt') : '';
      const badgeDescription = sale && discount > 0 ? ' Rabatt' : '';
      if (badge.textContent !== badgeText + badgeDescription) {
        badge.textContent = badgeText;
        if (badgeDescription) {
          const description = document.createElement('span');
          description.className = 'visually-hidden'; description.textContent = badgeDescription;
          badge.append(description);
        }
      }
      badge.removeAttribute('aria-label');
      oldPrice.removeAttribute('aria-label');
      const quantity = this.quantityInput();
      this.quantityButton.hidden = !quantity;
      write(this.quantityButton, quantity ? `Menge: ${quantity.value}` : '');
      if (quantity?.id) this.quantityButton.setAttribute('aria-controls', quantity.id);
      const sourceError = this.product.querySelector('product-form .product-form__error-message-wrapper');
      const errorMessage = hidden(sourceError) ? '' : text(sourceError?.querySelector('.product-form__error-message'));
      const error = this.querySelector('[data-cn-sticky-error]');
      write(error, errorMessage); error.hidden = !errorMessage;
      const rect = original.getBoundingClientRect();
      const overlay = overlayOpen();
      const keyboard = window.visualViewport && window.innerHeight - window.visualViewport.height > 160;
      if (overlay && this.contains(document.activeElement)) this.restoreFocus = true;
      this.show(rect.height > 0 && rect.bottom <= 0 && !hidden(original) && !overlay && !keyboard);
      if (!this.hidden && this.restoreFocus) {
        const active = document.activeElement;
        if (active === document.body || active?.closest('cart-drawer:not(.active), quick-add-modal:not([open]), product-modal:not([open]), modal-dialog:not([open])') || this.contains(active)) {
          const target = this.button.disabled && !this.quantityButton.hidden ? this.quantityButton : this.button;
          target.focus({ preventScroll: true });
        }
        this.restoreFocus = false;
      }
    }

    show(visible) {
      this.hidden = !visible;
      if (visible) {
        document.body.setAttribute('data-cn-sticky-purchase-active', '');
        document.documentElement.style.setProperty('--cn-sticky-purchase-space', `${Math.ceil(this.getBoundingClientRect().height)}px`);
      } else if (!document.querySelector('cn-sticky-purchase:not([hidden])')) {
        document.body.removeAttribute('data-cn-sticky-purchase-active');
        document.documentElement.style.removeProperty('--cn-sticky-purchase-space');
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect(); this.overlays?.disconnect(); this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.button?.removeEventListener('click', this.onBuy);
      this.quantityButton?.removeEventListener('click', this.onQuantity);
      this.product?.removeEventListener('input', this.schedule);
      this.product?.removeEventListener('change', this.schedule);
      window.removeEventListener('scroll', this.schedule);
      window.removeEventListener('resize', this.schedule);
      document.removeEventListener('focusin', this.onFocus);
      window.visualViewport?.removeEventListener('resize', this.schedule);
      this.show(false);
    }
  }
  customElements.define('cn-sticky-purchase', StickyPurchase);
})();
